/**
 * Shared types for the project view folder (src/app/views/project/).
 *
 * Follows the decomposition TEMPLATE established in src/app/views/work/
 * (see work.types.ts). The pattern:
 *
 * - ProjectController.ts — lifecycle hooks, route entries (init with
 *   :projectslug params), the controller's full public surface (everything
 *   AppController / SiteTransitions / NavMenuController touch), and ALL
 *   Events.on(...) registrations (registration order = delivery order on
 *   the static bus, so wiring physically stays in the controller; handlers
 *   delegate inward). Cross-controller references (this.get('...') /
 *   injected controllers / the globals.activeTile + globals.activeWorkController
 *   globals set by WorkController) are resolved here ONLY.
 * - ProjectView.ts — DOM element refs, the injected DOM structure template
 *   and the SidePaging module interop.
 * - ProjectMedia.ts — the header video/image switch (QueProjectEvent media
 *   half), header video play/pause/visibility and header sizing to the
 *   Pixi tile.
 * - ProjectScroll.ts — per-frame scroll state: circle progress, theme switch
 *   past the header, WebGL pause join, page-bottom / close-button visibility
 *   and the back-nav scroll position record.
 * - ProjectLoading.ts — the AJAX HTML record store + queued-injection join.
 * - project.types.ts — shared interfaces (this file).
 *
 * NOTE: the pre-existing satellite modules (SidePaging / VideoSwitch in
 * src/app/module/project/ and ProjectHeaderSizes in src/app/views/project/)
 * are dependencies of this view and intentionally stay where they are.
 *
 * Sub-objects are instance-scoped, receive narrow constructor-injected deps,
 * and talk back to the controller via the typed callbacks below or the
 * Events bus. They never hold a reference to the controller itself.
 */

/** Type of fullscreen header media a project page opens with */
export enum HeaderType {
	VIDEO = 'video',
	IMAGE = 'image'
}

/** AJAX loaded HTML for a single project page */
export interface ProjectHTMLRecord {
	url: string;
	html: string;
}

/** Narrow deps injected into ProjectMedia (implemented by ProjectController) */
export interface ProjectMediaDeps {
	/** Current scroll position (scrollbar controller — resolved in the controller) */
	getScroll(): number;
	/** Cached window inner height held by the controller */
	getInnerHeight(): number;
}

/** Narrow deps injected into ProjectScroll (implemented by ProjectController) */
export interface ProjectScrollDeps {
	isActive(): boolean;
	isPaused(): boolean;
	isDeeplink(): boolean;
	isClosing(): boolean;
	getActiveSlug(): string;
	getContainer(): Element;
	/** The circular close-progress element (queried by the controller on show) */
	getCircleProgressEl(): HTMLElement;
	getInnerHeight(): number;
	/** UI theme to restore when scrolling back above the header */
	getTopUITheme(): string;
	/** Switch the UI theme (controller — reaches the UI controller) */
	setUiThemeType(type: string): void;
	//Scrollbar controller interop (resolved in the controller):
	getRealProgress(): number;
	getMaxScroll(): number;
	isPixiScrollActive(): boolean;
	isNativeScroller(): boolean;
	getPlaneY(): number;
	//Work tile / WebGL interop during header scroll (globals.activeTile /
	//globals.activeWorkController resolve in the controller):
	scrollActiveTile(y: number): void;
	onWebGLPause(): void;
	onWebGLUnpause(): void;
}
